import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, 
  Cloud, 
  Upload, 
  FolderOpen, 
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Loader2,
  HardDrive,
  Wifi,
  X,
  Plus,
  FileImage,
  Settings,
  Eye,
  Link2,
  Unlink
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Liste des fournisseurs connus
const KNOWN_SUPPLIERS = [
  'DICKSON', 'SAULEDA', 'SATTLER', 'CITEL', 'LATIM', 
  'RECASENS', 'S&P', 'SERGE FERRARI'
];

// Mapping des noms de dossiers techniques vers les noms d'affichage
const FOLDER_DISPLAY_NAMES = {
  'setp': 'S&P',
  's_p': 'S&P',
  'serge_ferrari': 'SERGE FERRARI',
  'sergeferrari': 'SERGE FERRARI',
};

// Fonction pour convertir un nom de dossier technique en nom d'affichage
const getFolderDisplayName = (folderName) => {
  const lowerName = folderName.toLowerCase();
  if (FOLDER_DISPLAY_NAMES[lowerName]) {
    return FOLDER_DISPLAY_NAMES[lowerName];
  }
  return folderName.toUpperCase();
};

const AdminCloudinaryPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [cloudinaryFolders, setCloudinaryFolders] = useState([]);
  
  // États pour l'upload direct
  const [uploadSupplier, setUploadSupplier] = useState('');
  const [newSupplierName, setNewSupplierName] = useState('');
  const [showNewSupplierInput, setShowNewSupplierInput] = useState(false);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [uploadResults, setUploadResults] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  
  // États pour synchronisation
  const [syncing, setSyncing] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  
  // États pour la configuration du matching
  const [showMatchingConfig, setShowMatchingConfig] = useState(false);
  const [matchingSupplier, setMatchingSupplier] = useState('');
  const [cloudinaryImages, setCloudinaryImages] = useState([]);
  const [filteredImages, setFilteredImages] = useState([]);
  const [imageFilter, setImageFilter] = useState('');
  const [supplierProducts, setSupplierProducts] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [matchingRule, setMatchingRule] = useState({
    type: 'auto_numeric', // 'position', 'separator' ou 'auto_numeric'
    startPos: 0,
    endPos: 5,
    separator: '_',
    partIndex: 2,
    productField: 'ref_frs' // champ du produit à utiliser pour le matching
  });
  const [previewMatches, setPreviewMatches] = useState([]);
  const [editedCodes, setEditedCodes] = useState({}); // Codes manuellement édités par l'utilisateur
  const [expandFilenames, setExpandFilenames] = useState(false); // Afficher les noms complets
  
  const fileInputRef = useRef(null);
  const token = localStorage.getItem('admin_token');

  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/cloudinary/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Erreur status Cloudinary:', error);
      setStatus({ status: 'error', error: error.message });
    }
  }, [token]);

  const fetchCloudinaryFolders = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/cloudinary/folders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCloudinaryFolders(response.data.folders || []);
    } catch (error) {
      console.error('Erreur dossiers Cloudinary:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStatus(), fetchCloudinaryFolders()]);
      setLoading(false);
    };
    loadData();
  }, [fetchStatus, fetchCloudinaryFolders]);

  // Charger les images et produits pour la configuration du matching
  const loadMatchingData = async (supplier) => {
    setLoadingPreview(true);
    setMatchingSupplier(supplier);
    setShowMatchingConfig(true);
    setImageFilter(''); // Réinitialiser le filtre
    setEditedCodes({}); // Réinitialiser les codes édités
    
    try {
      // Charger les images Cloudinary du fournisseur
      const imagesResponse = await axios.get(
        `${BACKEND_URL}/api/admin/cloudinary/images/${encodeURIComponent(supplier)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const images = imagesResponse.data.images || [];
      setCloudinaryImages(images);
      setFilteredImages(images); // Initialiser aussi les images filtrées
      
      // Charger les produits du fournisseur
      const productsResponse = await axios.get(
        `${BACKEND_URL}/api/admin/cloudinary/products/${encodeURIComponent(supplier)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSupplierProducts(productsResponse.data.products || []);
      
    } catch (error) {
      console.error('Erreur chargement données matching:', error);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Extraire le code d'un nom de fichier selon la règle
  const extractCode = (filename, rule) => {
    // Enlever l'extension
    const nameWithoutExt = filename.replace(/\.[^.]+$/, '');
    
    if (rule.type === 'position') {
      return nameWithoutExt.substring(rule.startPos, rule.endPos);
    } else if (rule.type === 'separator') {
      const parts = nameWithoutExt.split(rule.separator);
      return parts[rule.partIndex] || '';
    } else if (rule.type === 'auto_numeric') {
      // Extraction automatique du premier code numérique à 5 chiffres (ou le plus long)
      const matches = nameWithoutExt.match(/\d{5}/g) || nameWithoutExt.match(/\d{4,}/g) || nameWithoutExt.match(/\d+/g) || [];
      return matches[0] || '';
    }
    return nameWithoutExt;
  };

  // Extraire uniquement les codes numériques d'une chaîne
  const extractNumericCodes = (str) => {
    const matches = str.match(/\d+/g) || [];
    return matches;
  };

  // Extraire le code d'un produit selon le champ sélectionné
  const extractProductCode = (product, field) => {
    const value = product[field] || '';
    // Extraire tous les nombres du champ
    const numbers = value.toString().match(/\d+/g) || [];
    return numbers;
  };

  // Calculer les correspondances en aperçu
  useEffect(() => {
    if (!showMatchingConfig || filteredImages.length === 0 || supplierProducts.length === 0) {
      setPreviewMatches([]);
      return;
    }
    
    const matches = [];
    const usedProducts = new Set();
    
    // Utiliser les images filtrées (ou toutes si pas de filtre)
    const imagesToProcess = filteredImages.slice(0, 200); // Limiter à 200 pour la preview
    
    for (const img of imagesToProcess) {
      const filename = img.public_id.split('/').pop();
      const autoExtractedCode = extractCode(filename, matchingRule);
      
      // Utiliser le code édité s'il existe, sinon le code auto-extrait
      const effectiveCode = editedCodes[filename] !== undefined ? editedCodes[filename] : autoExtractedCode;
      
      // Chercher un produit correspondant
      let matchedProduct = null;
      for (const product of supplierProducts) {
        if (usedProducts.has(product.id)) continue;
        
        const productCodes = extractProductCode(product, matchingRule.productField);
        
        // Vérifier si le code extrait correspond à un des codes du produit
        const codeMatch = productCodes.some(pc => {
          const normalizedExtracted = effectiveCode.replace(/^0+/, '') || '0';
          const normalizedProduct = pc.replace(/^0+/, '') || '0';
          return normalizedExtracted === normalizedProduct || 
                 effectiveCode === pc ||
                 effectiveCode.padStart(5, '0') === pc.padStart(5, '0');
        });
        
        if (codeMatch) {
          matchedProduct = product;
          usedProducts.add(product.id);
          break;
        }
      }
      
      matches.push({
        filename,
        autoExtractedCode,
        effectiveCode,
        isEdited: editedCodes[filename] !== undefined,
        matchedProduct,
        imageUrl: img.thumbnail_url || img.secure_url,
        publicId: img.public_id
      });
    }
    
    setPreviewMatches(matches);
  }, [filteredImages, supplierProducts, matchingRule, showMatchingConfig, editedCodes]);

  // Fonction pour éditer un code
  const handleEditCode = (filename, newCode) => {
    setEditedCodes(prev => ({
      ...prev,
      [filename]: newCode
    }));
  };

  // Réinitialiser les codes édités
  const resetEditedCodes = () => {
    setEditedCodes({});
  };

  // Filtrer les images quand le filtre change
  useEffect(() => {
    if (!imageFilter.trim()) {
      setFilteredImages(cloudinaryImages);
    } else {
      const filterLower = imageFilter.toLowerCase();
      const filtered = cloudinaryImages.filter(img => {
        const filename = img.public_id.split('/').pop().toLowerCase();
        return filename.includes(filterLower);
      });
      setFilteredImages(filtered);
    }
  }, [cloudinaryImages, imageFilter]);

  // Appliquer le matching avec la règle personnalisée ET les codes édités
  const applyCustomMatching = async () => {
    if (!matchingSupplier) return;
    
    setSyncing(true);
    setSyncResult(null);
    
    try {
      // Construire les correspondances depuis l'aperçu (incluant les codes édités)
      const manualMatches = previewMatches
        .filter(m => m.matchedProduct) // Seulement ceux qui ont une correspondance
        .map(m => ({
          image_public_id: m.publicId,
          image_url: m.imageUrl.replace('/c_fill,f_auto,g_auto,h_200,q_auto,w_200/', '/'),
          product_id: m.matchedProduct.id,
          extracted_code: m.effectiveCode,
          is_manual: m.isEdited
        }));
      
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/cloudinary/apply-matching`,
        {
          supplier: matchingSupplier,
          matches: manualMatches
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSyncResult(response.data);
      setShowMatchingConfig(false);
    } catch (error) {
      setSyncResult({ 
        error: error.response?.data?.detail || error.message 
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncSupplier = async (supplier) => {
    if (!window.confirm(`Synchroniser les images Cloudinary avec les produits ${supplier} ?`)) {
      return;
    }

    setSelectedSupplier(supplier);
    setSyncing(true);
    setSyncResult(null);

    try {
      const formData = new FormData();
      formData.append('supplier', supplier);

      const response = await axios.post(
        `${BACKEND_URL}/api/admin/cloudinary/sync-product-images`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSyncResult(response.data);
    } catch (error) {
      setSyncResult({ 
        error: error.response?.data?.detail || error.message 
      });
    } finally {
      setSyncing(false);
    }
  };

  // Gestion du drag & drop
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files).filter(
      file => file.type.startsWith('image/')
    );
    
    if (files.length > 0) {
      setFilesToUpload(prev => [...prev, ...files]);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files).filter(
      file => file.type.startsWith('image/')
    );
    
    if (files.length > 0) {
      setFilesToUpload(prev => [...prev, ...files]);
    }
  };

  const removeFile = (index) => {
    setFilesToUpload(prev => prev.filter((_, i) => i !== index));
  };

  const clearFiles = () => {
    setFilesToUpload([]);
    setUploadResults(null);
  };

  const getEffectiveSupplier = () => {
    if (showNewSupplierInput && newSupplierName.trim()) {
      return newSupplierName.trim().toUpperCase();
    }
    return uploadSupplier;
  };

  const handleUpload = async () => {
    const supplier = getEffectiveSupplier();
    
    if (!supplier) {
      alert('Veuillez sélectionner ou créer un fournisseur');
      return;
    }
    
    if (filesToUpload.length === 0) {
      alert('Veuillez sélectionner des images à uploader');
      return;
    }

    setUploading(true);
    setUploadProgress({ current: 0, total: filesToUpload.length });
    setUploadResults(null);

    const results = {
      success: 0,
      failed: 0,
      errors: [],
      uploaded: []
    };

    for (let i = 0; i < filesToUpload.length; i++) {
      const file = filesToUpload[i];
      setUploadProgress({ current: i + 1, total: filesToUpload.length });

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('supplier', supplier);

        const response = await axios.post(
          `${BACKEND_URL}/api/admin/cloudinary/upload`,
          formData,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            } 
          }
        );

        if (response.data.success) {
          results.success++;
          results.uploaded.push({
            filename: file.name,
            url: response.data.secure_url,
            thumbnail: response.data.thumbnail_url
          });
        } else {
          results.failed++;
          results.errors.push({ filename: file.name, error: 'Upload échoué' });
        }
      } catch (error) {
        results.failed++;
        results.errors.push({ 
          filename: file.name, 
          error: error.response?.data?.detail || error.message 
        });
      }
    }

    setUploadResults(results);
    setUploading(false);
    
    await fetchCloudinaryFolders();
    await fetchStatus();

    if (results.success > 0) {
      const syncNow = window.confirm(
        `${results.success} images uploadées avec succès.\n\nVoulez-vous configurer le matching avec les produits ${supplier} ?`
      );
      
      if (syncNow) {
        loadMatchingData(supplier);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-2">
              <Cloud className="text-blue-600" size={24} />
              <h1 className="text-xl font-bold">Gestion Images Cloudinary</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Modal de configuration du matching */}
        {showMatchingConfig && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header du modal */}
              <div className="p-4 border-b flex items-center justify-between bg-gray-50">
                <div className="flex items-center gap-2">
                  <Settings className="text-blue-600" size={24} />
                  <h2 className="text-lg font-bold">Configuration du matching - {matchingSupplier}</h2>
                </div>
                <button
                  onClick={() => setShowMatchingConfig(false)}
                  className="p-2 hover:bg-gray-200 rounded-full"
                >
                  <X size={20} />
                </button>
              </div>

              {loadingPreview ? (
                <div className="flex-1 flex items-center justify-center p-8">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  <span className="ml-2">Chargement des données...</span>
                </div>
              ) : (
                <div className="flex-1 overflow-auto p-4">
                  {/* Configuration de la règle */}
                  <div className="bg-blue-50 rounded-lg p-4 mb-4">
                    <h3 className="font-semibold text-blue-900 mb-3">📐 Règle d'extraction du code</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Type de règle */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Méthode d'extraction
                        </label>
                        <select
                          value={matchingRule.type}
                          onChange={(e) => setMatchingRule(prev => ({ ...prev, type: e.target.value }))}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="auto_numeric">🔢 Automatique (code numérique)</option>
                          <option value="position">Par position (caractères X à Y)</option>
                          <option value="separator">Par séparateur (partie N)</option>
                        </select>
                      </div>

                      {/* Champ du produit */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Champ du produit à comparer
                        </label>
                        <select
                          value={matchingRule.productField}
                          onChange={(e) => setMatchingRule(prev => ({ ...prev, productField: e.target.value }))}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="ref_frs">Référence fournisseur (ref_frs)</option>
                          <option value="code1">Code 1</option>
                          <option value="code2">Code 2</option>
                          <option value="code3">Code 3 (Nom)</option>
                        </select>
                      </div>

                      {/* Paramètres selon le type */}
                      {matchingRule.type === 'position' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Position de début
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={matchingRule.startPos}
                              onChange={(e) => setMatchingRule(prev => ({ ...prev, startPos: parseInt(e.target.value) || 0 }))}
                              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Position de fin
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={matchingRule.endPos}
                              onChange={(e) => setMatchingRule(prev => ({ ...prev, endPos: parseInt(e.target.value) || 1 }))}
                              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </>
                      )}
                      {matchingRule.type === 'separator' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Séparateur
                            </label>
                            <input
                              type="text"
                              value={matchingRule.separator}
                              onChange={(e) => setMatchingRule(prev => ({ ...prev, separator: e.target.value }))}
                              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                              placeholder="Ex: _ ou -"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Partie à utiliser (0 = première)
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={matchingRule.partIndex}
                              onChange={(e) => setMatchingRule(prev => ({ ...prev, partIndex: parseInt(e.target.value) || 0 }))}
                              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </>
                      )}
                      {matchingRule.type === 'auto_numeric' && (
                        <div className="col-span-2 p-3 bg-green-50 rounded-lg">
                          <p className="text-sm text-green-800">
                            ✨ <strong>Mode automatique :</strong> Extrait le premier code numérique à 5 chiffres du nom de fichier 
                            (ex: <code className="bg-green-100 px-1 rounded">00364</code> de <code className="bg-green-100 px-1 rounded">docril_collection_stripes_00364_...</code>)
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Exemple */}
                    {filteredImages.length > 0 && (
                      <div className="mt-4 p-3 bg-white rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">Exemple avec le premier fichier {imageFilter ? 'filtré' : ''} :</p>
                        <p className="font-mono text-sm">
                          <span className="text-gray-500">{filteredImages[0].public_id.split('/').pop()}</span>
                          {' → '}
                          <span className="font-bold text-blue-600">
                            "{extractCode(filteredImages[0].public_id.split('/').pop(), matchingRule)}"
                          </span>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Aperçu des correspondances */}
                  <div className="bg-white rounded-lg border">
                    <div className="p-3 border-b bg-gray-50">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold flex items-center gap-2">
                          <Eye size={18} />
                          Aperçu des correspondances
                        </h3>
                        <div className="text-sm text-gray-600">
                          {previewMatches.filter(m => m.matchedProduct).length} / {previewMatches.length} correspondances
                        </div>
                      </div>
                      
                      {/* Filtre et stats */}
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <input
                            type="text"
                            value={imageFilter}
                            onChange={(e) => setImageFilter(e.target.value)}
                            placeholder="🔍 Filtrer les fichiers (ex: docril, solid, 00001...)"
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div className="text-xs text-gray-500 whitespace-nowrap">
                          {filteredImages.length} / {cloudinaryImages.length} images
                        </div>
                      </div>
                    </div>
                    
                    <div className="max-h-[500px] overflow-auto">
                      <table className="w-full text-sm" style={{ minWidth: expandFilenames ? '1200px' : '800px' }}>
                        <thead className="bg-gray-100 sticky top-0">
                          <tr>
                            <th className="px-2 py-2 text-left w-14">Image</th>
                            <th className="px-2 py-2 text-left">
                              <div className="flex items-center gap-2">
                                <span>Fichier</span>
                                <button
                                  onClick={() => setExpandFilenames(!expandFilenames)}
                                  className={`px-2 py-0.5 text-xs rounded ${
                                    expandFilenames 
                                      ? 'bg-blue-600 text-white' 
                                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                  }`}
                                  title={expandFilenames ? 'Réduire' : 'Agrandir'}
                                >
                                  {expandFilenames ? '⬅️ Réduire' : '➡️ Agrandir'}
                                </button>
                              </div>
                            </th>
                            <th className="px-2 py-2 text-left w-28">Code extrait</th>
                            <th className="px-2 py-2 text-left">Correspondance</th>
                            <th className="px-2 py-2 text-center w-16">Statut</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewMatches.map((match, index) => (
                            <tr key={index} className={`border-b ${match.matchedProduct ? 'bg-green-50' : 'bg-red-50'}`}>
                              <td className="px-2 py-2">
                                <img 
                                  src={match.imageUrl} 
                                  alt="" 
                                  className="w-10 h-10 object-cover rounded"
                                />
                              </td>
                              <td className="px-2 py-2 font-mono text-xs">
                                {expandFilenames ? (
                                  <span className="whitespace-nowrap">{match.filename}</span>
                                ) : (
                                  <span title={match.filename} className="cursor-help">
                                    {match.filename.substring(0, 40)}{match.filename.length > 40 ? '...' : ''}
                                  </span>
                                )}
                              </td>
                              <td className="px-2 py-2">
                                <input
                                  type="text"
                                  value={match.effectiveCode}
                                  onChange={(e) => handleEditCode(match.filename, e.target.value)}
                                  className={`w-24 px-2 py-1 text-sm font-mono border rounded focus:ring-2 focus:ring-blue-500 ${
                                    match.isEdited ? 'bg-yellow-100 border-yellow-400' : 'bg-blue-50 border-blue-200'
                                  }`}
                                  title={match.isEdited ? `Édité (auto: ${match.autoExtractedCode})` : 'Code auto-extrait'}
                                />
                              </td>
                              <td className="px-2 py-2">
                                {match.matchedProduct ? (
                                  <div>
                                    <div className="font-medium text-sm">{match.matchedProduct.name?.substring(0, 35)}</div>
                                    <div className="text-xs text-gray-500">{match.matchedProduct.ref_frs}</div>
                                  </div>
                                ) : (
                                  <span className="text-red-600 text-sm">Aucune correspondance</span>
                                )}
                              </td>
                              <td className="px-2 py-2 text-center">
                                {match.matchedProduct ? (
                                  <Link2 className="text-green-600 mx-auto" size={18} />
                                ) : (
                                  <Unlink className="text-red-400 mx-auto" size={18} />
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Footer du modal */}
              <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  {cloudinaryImages.length} images • {supplierProducts.length} produits
                  {Object.keys(editedCodes).length > 0 && (
                    <span className="ml-2 text-yellow-600">
                      • {Object.keys(editedCodes).length} code(s) édité(s)
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  {Object.keys(editedCodes).length > 0 && (
                    <button
                      onClick={resetEditedCodes}
                      className="px-4 py-2 text-yellow-700 bg-yellow-100 rounded-lg hover:bg-yellow-200"
                    >
                      Réinitialiser
                    </button>
                  )}
                  <button
                    onClick={() => setShowMatchingConfig(false)}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-100"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={applyCustomMatching}
                    disabled={syncing || previewMatches.filter(m => m.matchedProduct).length === 0}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {syncing ? (
                      <><Loader2 className="animate-spin" size={16} /> Application...</>
                    ) : (
                      <><CheckCircle size={16} /> Appliquer le matching</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Statut Cloudinary */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Cloud className="text-blue-600" />
              Statut Cloudinary
            </h2>
            <button
              onClick={fetchStatus}
              className="p-2 hover:bg-gray-100 rounded-full"
              title="Rafraîchir"
            >
              <RefreshCw size={18} />
            </button>
          </div>

          {status?.status === 'connected' ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="text-green-600" size={24} />
                <div>
                  <p className="text-sm text-gray-600">Connexion</p>
                  <p className="font-semibold text-green-700">Connecté</p>
                  <p className="text-xs text-gray-500">{status.cloud_name}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                <HardDrive className="text-blue-600" size={24} />
                <div>
                  <p className="text-sm text-gray-600">Stockage</p>
                  <p className="font-semibold">{status.storage?.used_mb || 0} MB</p>
                  <p className="text-xs text-gray-500">
                    {status.storage?.percentage || 0}% utilisé
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg">
                <Wifi className="text-purple-600" size={24} />
                <div>
                  <p className="text-sm text-gray-600">Bande passante</p>
                  <p className="font-semibold">{status.bandwidth?.used_mb || 0} MB</p>
                  <p className="text-xs text-gray-500">ce mois</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg">
              <AlertCircle className="text-red-600" size={24} />
              <div>
                <p className="font-semibold text-red-700">Erreur de connexion</p>
                <p className="text-sm text-red-600">{status?.error}</p>
              </div>
            </div>
          )}
        </div>

        {/* UPLOAD DIRECT */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border-2 border-blue-200">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Upload className="text-blue-600" />
            📤 Upload Direct vers Cloudinary
          </h2>

          {/* Sélection du fournisseur */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              1. Choisir le fournisseur
            </label>
            <div className="flex gap-2 flex-wrap">
              {KNOWN_SUPPLIERS.map(supplier => (
                <button
                  key={supplier}
                  onClick={() => {
                    setUploadSupplier(supplier);
                    setShowNewSupplierInput(false);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    uploadSupplier === supplier && !showNewSupplierInput
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {supplier}
                </button>
              ))}
              <button
                onClick={() => {
                  setShowNewSupplierInput(true);
                  setUploadSupplier('');
                }}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                  showNewSupplierInput
                    ? 'bg-green-600 text-white'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                <Plus size={14} /> Nouveau
              </button>
            </div>
            
            {showNewSupplierInput && (
              <input
                type="text"
                value={newSupplierName}
                onChange={(e) => setNewSupplierName(e.target.value)}
                placeholder="Nom du nouveau fournisseur..."
                className="mt-2 w-full max-w-xs px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            )}
          </div>

          {/* Zone de drop */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              2. Sélectionner les images
            </label>
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                dragActive 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
              }`}
            >
              <FileImage className="mx-auto mb-3 text-gray-400" size={48} />
              <p className="text-gray-600 mb-1">
                <span className="font-semibold text-blue-600">Cliquez</span> ou glissez-déposez vos images ici
              </p>
              <p className="text-sm text-gray-500">
                JPG, PNG, WebP acceptés
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Liste des fichiers sélectionnés */}
          {filesToUpload.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">
                  {filesToUpload.length} image(s) sélectionnée(s)
                </label>
                <button
                  onClick={clearFiles}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Tout supprimer
                </button>
              </div>
              <div className="max-h-40 overflow-y-auto border rounded-lg p-2 bg-gray-50">
                {filesToUpload.map((file, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between py-1 px-2 hover:bg-white rounded"
                  >
                    <span className="text-sm text-gray-700 truncate flex-1">
                      {file.name}
                    </span>
                    <span className="text-xs text-gray-500 mx-2">
                      {(file.size / 1024).toFixed(0)} KB
                    </span>
                    <button
                      onClick={() => removeFile(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bouton d'upload */}
          <button
            onClick={handleUpload}
            disabled={uploading || filesToUpload.length === 0 || !getEffectiveSupplier()}
            className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {uploading ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                Upload en cours... ({uploadProgress.current}/{uploadProgress.total})
              </>
            ) : (
              <>
                <Upload size={20} />
                Uploader vers Cloudinary
              </>
            )}
          </button>

          {/* Résultats d'upload */}
          {uploadResults && (
            <div className={`mt-4 p-4 rounded-lg ${
              uploadResults.failed > 0 ? 'bg-yellow-50' : 'bg-green-50'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                {uploadResults.failed > 0 ? (
                  <AlertCircle className="text-yellow-600" size={20} />
                ) : (
                  <CheckCircle className="text-green-600" size={20} />
                )}
                <span className="font-semibold">
                  {uploadResults.success} uploadée(s), {uploadResults.failed} échec(s)
                </span>
              </div>
              {uploadResults.errors.length > 0 && (
                <div className="text-sm text-red-600 mt-2">
                  {uploadResults.errors.map((err, i) => (
                    <div key={i}>• {err.filename}: {err.error}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Résultat de synchronisation */}
        {syncResult && (
          <div className={`rounded-lg shadow-sm p-6 mb-6 ${
            syncResult.error ? 'bg-red-50' : 'bg-green-50'
          }`}>
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              {syncResult.error ? (
                <><AlertCircle className="text-red-600" /> Erreur</>
              ) : (
                <><CheckCircle className="text-green-600" /> Synchronisation terminée</>
              )}
            </h3>
            {syncResult.error ? (
              <p className="text-red-700">{syncResult.error}</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Images Cloudinary</p>
                  <p className="font-semibold">{syncResult.cloudinary_images || 0}</p>
                </div>
                <div>
                  <p className="text-gray-600">Produits trouvés</p>
                  <p className="font-semibold">{syncResult.total_products || 0}</p>
                </div>
                <div>
                  <p className="text-gray-600">Produits mis à jour</p>
                  <p className="font-semibold text-green-700">{syncResult.products_matched || 0}</p>
                </div>
                <div>
                  <p className="text-gray-600">Taux de correspondance</p>
                  <p className="font-semibold text-blue-700">
                    {syncResult.total_products > 0 
                      ? Math.round((syncResult.products_matched / syncResult.total_products) * 100) 
                      : 0}%
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Dossiers Cloudinary */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Cloud className="text-blue-600" />
              Dossiers Cloudinary
            </h2>
            <button
              onClick={fetchCloudinaryFolders}
              className="p-2 hover:bg-gray-100 rounded-full"
              title="Rafraîchir"
            >
              <RefreshCw size={18} />
            </button>
          </div>
          
          {cloudinaryFolders.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              Aucun dossier sur Cloudinary.<br />
              Uploadez des images pour créer des dossiers.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {cloudinaryFolders.map((folder) => {
                const displayName = getFolderDisplayName(folder);
                return (
                  <div 
                    key={folder}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-3">
                      <FolderOpen className="text-blue-400" size={20} />
                      <p className="font-medium">{displayName}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => loadMatchingData(displayName)}
                        className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1"
                        title="Configurer le matching"
                      >
                        <Settings size={14} /> Configurer
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-6 bg-blue-50 rounded-lg p-6">
          <h3 className="font-semibold text-blue-900 mb-3">📖 Comment utiliser ?</h3>
          <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
            <li>Uploadez vos images (glissez-déposez ou cliquez)</li>
            <li>Cliquez sur <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-600 text-white rounded text-xs"><Settings size={12} /> Configurer</span> à côté du fournisseur</li>
            <li>Vérifiez les correspondances dans l'aperçu</li>
            <li>Éditez manuellement les codes si nécessaire</li>
            <li>Cliquez "Appliquer le matching"</li>
          </ol>
          <div className="mt-4 p-3 bg-yellow-100 rounded-lg">
            <p className="text-yellow-800 text-sm">
              ⚠️ <strong>Important :</strong> Les correspondances affichées dans l'aperçu sont exactement celles qui seront appliquées. 
              Les codes édités manuellement ont la priorité.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminCloudinaryPage;
